// Column definitions for the People and Companies grids.
//
// A column is a tiny render contract — header label, optional sort key,
// width, and a function that turns a Contact row into a React node.
// Kept as plain data so a future "saved view" feature (phase 4) can
// pick a subset by id.

import type { Contact } from '@personical/cc-shared-types';
import {
  CompanyRefCell,
  DateCell,
  EmailCell,
  NameCell,
  PhoneCell,
  StarCell,
  TagsCell
} from '../cells/index.js';
import type { GridColumn } from '../types.js';

export interface ContactGridContext {
  currentOid: string | null;
  /** Toggle the current user's star on a row. */
  onToggleStar(rowId: string, value: boolean): Promise<void>;
}

// Kept as a re-export so existing imports in cells/index.tsx don't break.
export type { GridColumn };

export const PEOPLE_COLUMNS: GridColumn<Contact, ContactGridContext>[] = [
  {
    id: 'star',
    label: '',
    width: '2.25rem',
    render: (row, ctx) => <StarCell row={row} ctx={ctx} />
  },
  {
    id: 'name',
    label: 'Name',
    width: '20rem',
    sortKey: 'name',
    render: (row, ctx) => <NameCell row={row} currentOid={ctx.currentOid} />
  },
  {
    id: 'company',
    label: 'Company',
    width: '8rem',
    mobile: 'hide',
    render: (row) => <CompanyRefCell row={row} />
  },
  {
    id: 'email',
    label: 'Email',
    width: '16rem',
    mobile: 'hide',
    render: (row) => <EmailCell row={row} />
  },
  {
    id: 'phone',
    label: 'Phone',
    width: '11rem',
    render: (row) => <PhoneCell row={row} />
  },
  {
    id: 'tags',
    label: 'Tags',
    mobile: 'hide',
    render: (row) => <TagsCell row={row} />
  },
  {
    id: 'updated',
    label: 'Updated',
    width: '8rem',
    sortKey: 'updated',
    mobile: 'hide',
    render: (row) => <DateCell value={row.updatedAt} />
  }
];

export const COMPANY_COLUMNS: GridColumn<Contact, ContactGridContext>[] = [
  {
    id: 'star',
    label: '',
    width: '2.25rem',
    render: (row, ctx) => <StarCell row={row} ctx={ctx} />
  },
  {
    id: 'name',
    label: 'Company',
    width: '22rem',
    sortKey: 'name',
    render: (row, ctx) => <NameCell row={row} currentOid={ctx.currentOid} />
  },
  {
    id: 'email',
    label: 'Email',
    width: '16rem',
    mobile: 'hide',
    render: (row) => <EmailCell row={row} />
  },
  {
    id: 'phone',
    label: 'Phone',
    width: '11rem',
    mobile: 'hide',
    render: (row) => <PhoneCell row={row} />
  },
  {
    id: 'tags',
    label: 'Tags',
    mobile: 'hide',
    render: (row) => <TagsCell row={row} />
  },
  {
    id: 'created',
    label: 'Added',
    width: '8rem',
    sortKey: 'recent',
    mobile: 'hide',
    render: (row) => <DateCell value={row.createdAt} />
  }
];
